import React, { useRef, useEffect } from 'react';
import classNames from 'classnames';
import { ActiveFilters, SortSection, Title, ProductFilters, ProductGrid, DiscontinuedSection } from './components';
import { useScreenSize } from '../../../helpers';
import { useProductFilter } from './utils';
import './ProductSeries.scss';
import MobileProductFilters from './components/MobileProductFilters';
import { miniGazelle } from '../../../helpers';
import { updateHistory } from '../PartnerAssetList/helpers';
import PropTypes from 'prop-types';

const ProductSeries = ({ data, location, hasDiscontinued, filters }) => {
    const {
        title,
        logicalName,
        genericName,
        showTitle = false,
        customTitle,
        customDescription,
        showsDiscontinued,
        hasOnlyRetiredProducts,
        sort: sortProp,
        sortBy: sortByProp = [],
        products: rawProducts = [],
        showFilters = true,
    } = data;

    const {
        products,
        productFilters,
        visibleOptions,
        activeFilters,
        toggleFilterVisibility,
        filterProducts,
        clearFilters,
        isFilterOptionChecked,
        setActiveFilters,
        setProductFilters,
        getMobileFilteredProductFilters,
        mobileFilterProducts,
        setProducts,
    } = useProductFilter({
        rawProducts,
        filters,
    });

    const { isMobileScreen } = useScreenSize();
    const filterRef = useRef([]);

    const shouldShowFilters = showFilters && filters?.length > 0;
    const shouldShowSorter = sortProp && sortByProp.length > 0;

    // Setup query params to persist filter options
    useEffect(() => {
        if (activeFilters?.length > 0 && shouldShowFilters) {
            const queryString = activeFilters
                .map(filter => {
                    if (filter.name && filter.option && filter.attributeId) {
                        const sanitizedOption = filter.option.replace(
                            /(?<=\d\")\+|(\")|(\(\d+cm\+?\))/g,
                            (match, quote, cmGroup) => (cmGroup ? cmGroup.replace(/\+/, '') : '')
                        );

                        return `${filter.attributeId}=${encodeURIComponent(
                            sanitizedOption
                        )}&filterKey=${encodeURIComponent(filter.name)}`;
                    }
                    return null;
                })
                .filter(Boolean)
                .join('&');

            updateHistory(queryString, {
                location,
            });
        }
    }, [activeFilters]);

    // Use query params to restore user filter selection
    useEffect(() => {
        const queryString = location.search;
        if (queryString && shouldShowFilters) {
            const params = new URLSearchParams(queryString.slice(1));

            const formattedFilters = Array.from(params.entries()).reduce((acc, [key, value]) => {
                if(key === 'discontinued') return acc
                if (key === 'filterKey') return acc;

                const decodedValue = decodeURIComponent(value);

                const reintegratedValue = decodedValue.replace(
                    /(\d+)(\s*-\s*)(\d+)|(<\s*\d+)|(\d+)\s*\(([^)]+)\)/,
                    (match, p1, p2, p3, p4, p5, p6) => {
                        if (p4) return `${p4}"`; // Handle < 40 format
                        if (p5 && p6) return `${p5}"+ (${p6}+)`; // Handle 90 (229cm) as 90"+ (229cm+)
                        return `${p1}" - ${p3}"`; // Handle 40 - 49 format
                    }
                );

                const getFilterKey = selectedOption =>
                    productFilters.find(filter => filter.options.includes(selectedOption))?.filterKey || null;

                const filterKey = getFilterKey(reintegratedValue);

                acc.push({
                    attributeId: decodeURIComponent(key),
                    option: filterKey === 'screenSizeRange' ? reintegratedValue : decodedValue,
                    name: getFilterKey(filterKey === 'screenSizeRange' ? reintegratedValue : decodedValue),
                });

                return acc;
            }, []);

            setActiveFilters(formattedFilters);

            const newProducts = mobileFilterProducts(formattedFilters, []);
            const newFilters = getMobileFilteredProductFilters(formattedFilters, productFilters);

            const uniqueFilterKeys = new Set();

            formattedFilters.forEach(filter => {
                const { name } = filter;

                if (!uniqueFilterKeys.has(name)) {
                    const index = productFilters.findIndex(productFilter => productFilter.filterKey === name);

                    if (index !== -1) {
                        if(index !=0) {
                        toggleFilterVisibility(index);
                        }
                        uniqueFilterKeys.add(name);
                    }
                }
            });

            setProductFilters(newFilters);
            setProducts(newProducts);
        }
    }, []);

    const productWrapperClasses = classNames('product-wrapper', {
        'has-title': showTitle,
        'no-filters': !shouldShowFilters,
    });

    const productLayoutClasses = classNames('product-layout', {
        container: !isMobileScreen,
        'no-filters': !shouldShowFilters,
        'with-sorter': shouldShowSorter,
    });

    const discontinuedCurrentLabel = showsDiscontinued ? 'showCurrent' : 'showDiscontinued';

    // Component Render
    return (
        <div className="container-fluid module product-series" id={logicalName} data-label={genericName}>
            {/* Title Section */}
            {showTitle && (
                <Title
                    title={title}
                    customTitle={customTitle}
                    genericName={genericName}
                    customDescription={customDescription}
                    data={data}
                />
            )}

            {/* Sort Section */}
            {shouldShowSorter && (
                <div className="container">
                    <SortSection
                        products={products}
                        sortByProp={sortByProp}
                        setProducts={sortedProducts => setProducts(sortedProducts)}
                    />
                </div>
            )}

            {/* Product Layout Section */}
            <div className={productLayoutClasses}>
                {/* Active Filters for Desktop */}
                {!isMobileScreen && shouldShowFilters && (
                    <ActiveFilters
                        activeFilters={activeFilters}
                        clearCurrentFilters={clearFilters}
                        filterProducts={filterProducts}
                        location={location}
                    />
                )}

                {/* Filters Section */}
                {shouldShowFilters &&
                    (!isMobileScreen ? (
                        <ProductFilters
                            productFilters={productFilters}
                            visibleOptions={visibleOptions}
                            toggleFilterVisibility={toggleFilterVisibility}
                            filterProducts={filterProducts}
                            isFilterOptionChecked={isFilterOptionChecked}
                        />
                    ) : (
                        <MobileProductFilters
                            productFilters={productFilters}
                            visibleOptions={visibleOptions}
                            toggleFilterVisibility={toggleFilterVisibility}
                            filterRef={filterRef}
                            filterProducts={mobileFilterProducts}
                            clearCurrentFilters={clearFilters}
                            activeFilters={activeFilters}
                            setActiveFilters={setActiveFilters}
                            getFilteredProductFilters={getMobileFilteredProductFilters}
                            setProductFilters={setProductFilters}
                        />
                    ))}

                {/* Product Count */}
                {shouldShowFilters && (
                    <div className="product-count">
                        <p>
                            {products.length}{' '}
                            {products.length === 1
                                ? miniGazelle.label('productFound', true)
                                : miniGazelle.label('productsFound', true)}
                        </p>
                    </div>
                )}

                {/* Product Grid */}
                <ProductGrid
                    products={products}
                    productWrapperClasses={productWrapperClasses}
                    shouldShowFilters={shouldShowFilters}
                />

                {/* Discontinued Section */}
                {(hasDiscontinued || showsDiscontinued) && !hasOnlyRetiredProducts && (
                    <DiscontinuedSection
                        location={location}
                        showsDiscontinued={showsDiscontinued}
                        discontinuedCurrentLabel={discontinuedCurrentLabel}
                    />
                )}
            </div>
        </div>
    );
};

ProductSeries.propTypes = {
    data: PropTypes.shape({
        title: PropTypes.string,
        logicalName: PropTypes.string,
        genericName: PropTypes.string,
        showTitle: PropTypes.bool,
        customTitle: PropTypes.string,
        customDescription: PropTypes.string,
        showsDiscontinued: PropTypes.bool,
        hasOnlyRetiredProducts: PropTypes.bool,
        sort: PropTypes.bool,
        sortBy: PropTypes.array,
        products: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                link: PropTypes.string,
                thumbnail: PropTypes.string,
                description: PropTypes.string,
                price: PropTypes.arrayOf(PropTypes.shape({
                    value: PropTypes.number,
                    currency: PropTypes.string,
                    label: PropTypes.string,
                })),
                isDiscontinued: PropTypes.bool,
                isNewProduct: PropTypes.bool,
                filterFields: PropTypes.shape({
                    screenSize: PropTypes.string,
                    screenSizeRange: PropTypes.string,
                    brightness: PropTypes.string,
                    brightnessRange: PropTypes.string,
                    features: PropTypes.arrayOf(PropTypes.string),
                })
            })
        ),
        showFilters: PropTypes.bool,
    }),
    hasDiscontinued: PropTypes.bool,
    location: PropTypes.shape({
        search: PropTypes.string,
        pathname: PropTypes.string,
        hash: PropTypes.string,
    }),
    filters: PropTypes.arrayOf(
        PropTypes.shape({
            attributeId: PropTypes.string,
            options: PropTypes.array,
            filterKey: PropTypes.string,
        })
    )
}

export default ProductSeries;
